import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Home, Upload, X, Sparkles, DollarSign } from "lucide-react";
import { toast } from "sonner";
import PriceComparison from "@/components/PriceComparison";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateListing = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [counties, setCounties] = useState([]);
  const [showPriceComparison, setShowPriceComparison] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    address: "",
    city: "",
    zip_code: "",
    county: "",
    bedrooms: "",
    bathrooms: "",
    sqft: "",
    lot_size: "",
    year_built: "",
    property_type: "Single Family",
    features: "",
    // Financial
    annual_property_tax: "",
    taxing_authority: "",
    annual_home_insurance: "",
    monthly_home_insurance: "",
    hoa_fee: "",
    hoa_fee_frequency: "monthly",
    owner_financing: false,
    assumable_loan: "",
    // Systems
    hvac_age: "",
    hvac_type: "",
    roof_age: "",
    roof_material: "",
    water_source: "",
    sewer_system: "",
    electrical_type: "",
    plumbing_type: "",
    foundation_type: "",
    // Utilities
    utilities: [],
    heating_fuel: "",
    has_solar_panels: false,
    solar_panel_year_installed: "",
    solar_panel_ownership: "",
    sewer_type: "",
    monthly_electric_cost: "",
    monthly_gas_cost: "",
    monthly_water_sewer_cost: "",
    // Additional
    garage_spaces: "",
    garage_type: "",
    has_pool: false,
    pool_type: "",
    school_district: "",
    elementary_school: "",
    middle_school: "",
    high_school: "",
    zoning: "",
    appliances_included: "",
    recent_upgrades: "",
    seller_motivation: "",
    // Media
    video_url: "",
    virtual_tour_url: "",
    // Contact
    contact_name: user?.name || "",
    contact_email: user?.email || "",
    contact_phone: "",
    preferred_contact_method: ["email"],
    preferred_contact_time: "anytime"
  });

  const fetchCounties = async () => {
    try {
      const response = await axios.get(`${API}/data/counties`);
      setCounties(response.data);
    } catch (error) {
      console.error("Failed to fetch counties");
    }
  };

  useEffect(() => {
    fetchCounties();
  }, []);

  const onDrop = useCallback(acceptedFiles => {
    acceptedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setPhotos(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    maxSize: 10485760, // 10MB
    maxFiles: 50
  });

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const movePhoto = (fromIndex, toIndex) => {
    const newPhotos = [...photos];
    const [movedPhoto] = newPhotos.splice(fromIndex, 1);
    newPhotos.splice(toIndex, 0, movedPhoto);
    setPhotos(newPhotos);
  };

  const handlePhotoDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index);
  };

  const handlePhotoDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handlePhotoDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/html'));
    if (dragIndex !== dropIndex) {
      movePhoto(dragIndex, dropIndex);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.description || !formData.price || !formData.address || !formData.city || !formData.county || !formData.zip_code) {
      toast.error("Please fill all required fields");
      return;
    }

    if (photos.length === 0) {
      toast.error("Please upload at least one photo");
      return;
    }

    setLoading(true);
    try {
      const listingData = {
        ...formData,
        price: parseFloat(formData.price),
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseFloat(formData.bathrooms),
        sqft: parseInt(formData.sqft),
        lot_size: formData.lot_size ? parseFloat(formData.lot_size) : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        features: formData.features ? formData.features.split(',').map(f => f.trim()) : [],
        photos: photos
      };

      const response = await axios.post(`${API}/listings`, listingData, {
        withCredentials: true
      });

      toast.success("Listing created successfully! 🎉");
      navigate(`/listing/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create listing");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="outline" data-testid="back-to-dashboard-btn">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display' }}>
            List Your DFW Home For Sale By Owner (FSBO)
          </h1>
          <p className="text-lg text-gray-700 mb-2 font-semibold">
            Dallas-Fort Worth FSBO Listings • No real estate agents or broker competition
          </p>
          <p className="text-base text-gray-600">
            Add your Texas property details and let AI enhance your listing for FREE
          </p>
          <div className="mt-4 inline-flex items-center space-x-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm">
            <Sparkles className="w-4 h-4" />
            <span>AI will generate an enhanced description and price suggestion</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 border border-orange-100">
          {/* Photos Upload */}
          <div className="mb-8">
            <label className="block text-lg font-semibold text-gray-900 mb-4">Property Photos *</label>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                isDragActive ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:border-orange-400'
              }`}
              data-testid="photo-dropzone"
            >
              <input {...getInputProps()} />
              <Upload className="w-12 h-12 mx-auto mb-4 text-orange-600" />
              {isDragActive ? (
                <p className="text-lg text-orange-600">Drop photos here...</p>
              ) : (
                <>
                  <p className="text-lg text-gray-900 font-medium mb-2">
                    Drag & drop photos here, or click to select
                  </p>
                  <p className="text-sm text-gray-500">
                    Up to 50 photos, 10MB each. JPG, PNG, or WEBP
                  </p>
                </>
              )}
            </div>
            {photos.length > 0 && (
              <>
                <p className="mt-4 text-sm text-gray-600 font-medium">
                  ✨ Drag photos to reorder • First photo will be the main listing image
                </p>
                <div className="mt-2 grid grid-cols-4 gap-4">
                  {photos.map((photo, idx) => (
                    <div 
                      key={idx} 
                      className="relative group cursor-move"
                      draggable
                      onDragStart={(e) => handlePhotoDragStart(e, idx)}
                      onDragOver={handlePhotoDragOver}
                      onDrop={(e) => handlePhotoDrop(e, idx)}
                    >
                      <img
                        src={photo}
                        alt={`Preview ${idx + 1}`}
                        className="w-full h-24 object-cover rounded-lg border-2 border-gray-200 hover:border-orange-400 transition-all"
                        data-testid={`photo-preview-${idx}`}
                      />
                      <div className="absolute top-1 left-1 bg-orange-600 text-white px-2 py-0.5 rounded text-xs font-bold">
                        {idx + 1}
                        {idx === 0 && " 🌟"}
                      </div>
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-photo-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Professional Services Reminder */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500 p-4 rounded-r-lg mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-orange-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h4 className="text-sm font-bold text-orange-900 mb-2">💡 Need Professional Help?</h4>
                <p className="text-sm text-orange-800 mb-3">
                  Great photos sell homes faster! We've partnered with local DFW professionals to help you:
                </p>
                <ul className="text-sm text-orange-800 space-y-1 mb-3">
                  <li>📷 <strong>Professional Photographers</strong> - High-quality listing photos</li>
                  <li>🎥 <strong>Videographers</strong> - Virtual tours & drone footage</li>
                  <li>🏠 <strong>Home Stagers</strong> - Make your home show-ready</li>
                  <li>🧹 <strong>Deep Cleaning Services</strong> - Pre-listing cleaning</li>
                  <li>🔧 <strong>Home Inspectors</strong> - Pre-listing inspections</li>
                  <li>⚖️ <strong>Real Estate Attorneys</strong> - Contract review</li>
                </ul>
                <a 
                  href="/professional-services" 
                  target="_blank"
                  className="inline-flex items-center text-sm font-semibold text-orange-700 hover:text-orange-900 underline"
                >
                  View All Professional Services →
                </a>
              </div>
            </div>
          </div>

          {/* Video & Virtual Tour */}
          <div className="space-y-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 border-b pb-3">📹 Video & Virtual Tour</h3>
            <p className="text-sm text-gray-600">Add video links to showcase your property (optional but highly recommended)</p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Property Video URL</label>
              <Input
                value={formData.video_url || ''}
                onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                data-testid="input-video-url"
              />
              <p className="text-xs text-gray-500 mt-1">
                YouTube, Vimeo, or any video hosting platform. Listings with videos get 4x more views!
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Virtual Tour URL</label>
              <Input
                value={formData.virtual_tour_url || ''}
                onChange={(e) => setFormData({...formData, virtual_tour_url: e.target.value})}
                placeholder="https://my.matterport.com/... or 3D tour link"
                data-testid="input-virtual-tour-url"
              />
              <p className="text-xs text-gray-500 mt-1">
                Matterport, Zillow 3D Home, or other virtual tour links
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">💡 Pro Tip: Video = More Serious Buyers</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Properties with videos sell 12% faster on average</li>
                <li>• Use your smartphone - no need for professional equipment</li>
                <li>• Walk through each room, showing natural light and flow</li>
                <li>• Upload to YouTube (free) or use your phone's Photos app to create a slideshow</li>
              </ul>
            </div>
          </div>

          {/* Basic Info */}
          <div className="space-y-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 border-b pb-3">Basic Information</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Property Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Beautiful 4BR Home in Frisco"
                data-testid="input-title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe your property..."
                rows={6}
                data-testid="input-description"
              />
              <p className="text-xs text-gray-500 mt-1">AI will enhance this description for you</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Asking Price * ($)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  placeholder="325000"
                  className="pl-10"
                  data-testid="input-price"
                />
              </div>
              <Button
                type="button"
                onClick={() => setShowPriceComparison(!showPriceComparison)}
                variant="outline"
                className="mt-2 w-full border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {showPriceComparison ? 'Hide' : 'Show'} Smart Price Analyzer
              </Button>
              <p className="text-xs text-gray-500 mt-1">Compare to recent sales in your area</p>
            </div>
          </div>

          {/* Price Comparison Tool */}
          {showPriceComparison && (
            <div className="mb-8">
              <PriceComparison
                initialData={{
                  address: formData.address,
                  zipcode: formData.zip_code,
                  sqft: formData.sqft,
                  beds: formData.bedrooms,
                  baths: formData.bathrooms
                }}
                onPriceSelect={(price) => {
                  setFormData({...formData, price: price.toString()});
                }}
              />
            </div>
          )}

          {/* Financial Details */}
          <div className="space-y-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 border-b pb-3">💰 Financial Details</h3>
            <p className="text-sm text-gray-600">Help buyers understand the total cost of ownership</p>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-gray-900 mb-3">Property Taxes</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Annual Property Tax ($)</label>
                  <Input
                    type="number"
                    value={formData.annual_property_tax}
                    onChange={(e) => setFormData({...formData, annual_property_tax: e.target.value})}
                    placeholder="8500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Yearly property tax amount</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Taxing Authority</label>
                  <Input
                    value={formData.taxing_authority}
                    onChange={(e) => setFormData({...formData, taxing_authority: e.target.value})}
                    placeholder="e.g., Collin County, Dallas County"
                  />
                  <p className="text-xs text-gray-500 mt-1">County or city tax office</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-gray-900 mb-3">Home Insurance</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Annual Insurance ($)</label>
                  <Input
                    type="number"
                    value={formData.annual_home_insurance}
                    onChange={(e) => {
                      const annual = e.target.value;
                      const monthly = annual ? (parseFloat(annual) / 12).toFixed(2) : "";
                      setFormData({
                        ...formData, 
                        annual_home_insurance: annual,
                        monthly_home_insurance: monthly
                      });
                    }}
                    placeholder="2400"
                  />
                  <p className="text-xs text-gray-500 mt-1">Your current yearly insurance cost</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Insurance ($)</label>
                  <Input
                    type="number"
                    value={formData.monthly_home_insurance}
                    onChange={(e) => {
                      const monthly = e.target.value;
                      const annual = monthly ? (parseFloat(monthly) * 12).toFixed(2) : "";
                      setFormData({
                        ...formData, 
                        monthly_home_insurance: monthly,
                        annual_home_insurance: annual
                      });
                    }}
                    placeholder="200"
                  />
                  <p className="text-xs text-gray-500 mt-1">Calculated from annual</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-gray-900 mb-3">HOA Fees</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">HOA Fee Amount ($)</label>
                  <Input
                    type="number"
                    value={formData.hoa_fee}
                    onChange={(e) => setFormData({...formData, hoa_fee: e.target.value})}
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter 0 if no HOA</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Frequency</label>
                  <Select value={formData.hoa_fee_frequency} onValueChange={(val) => setFormData({...formData, hoa_fee_frequency: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Utilities */}
          <div className="space-y-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 border-b pb-3">⚡ Utilities & Energy</h3>
            
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <h4 className="font-semibold text-gray-900 mb-3">Available Utilities (check all that apply)</h4>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.utilities.includes('electric')}
                    onChange={(e) => {
                      const utilities = e.target.checked 
                        ? [...formData.utilities, 'electric']
                        : formData.utilities.filter(u => u !== 'electric');
                      setFormData({...formData, utilities});
                    }}
                    className="mr-2 h-4 w-4"
                  />
                  <span className="text-sm">⚡ Electric</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.utilities.includes('natural_gas')}
                    onChange={(e) => {
                      const utilities = e.target.checked 
                        ? [...formData.utilities, 'natural_gas']
                        : formData.utilities.filter(u => u !== 'natural_gas');
                      setFormData({...formData, utilities});
                    }}
                    className="mr-2 h-4 w-4"
                  />
                  <span className="text-sm">🔥 Natural Gas</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.utilities.includes('propane')}
                    onChange={(e) => {
                      const utilities = e.target.checked 
                        ? [...formData.utilities, 'propane']
                        : formData.utilities.filter(u => u !== 'propane');
                      setFormData({...formData, utilities});
                    }}
                    className="mr-2 h-4 w-4"
                  />
                  <span className="text-sm">🔋 Propane</span>
                </label>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-gray-900 mb-3">Water Source</h4>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="water_source"
                    checked={formData.water_source === 'city_water'}
                    onChange={() => setFormData({...formData, water_source: 'city_water'})}
                    className="mr-2"
                  />
                  <span className="text-sm">💧 City Water</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="water_source"
                    checked={formData.water_source === 'well_water'}
                    onChange={() => setFormData({...formData, water_source: 'well_water'})}
                    className="mr-2"
                  />
                  <span className="text-sm">🚰 Well Water (Private)</span>
                </label>
              </div>
            </div>

            <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
              <h4 className="font-semibold text-gray-900 mb-3">Sewer System</h4>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="sewer_type"
                    checked={formData.sewer_type === 'city_sewer'}
                    onChange={() => setFormData({...formData, sewer_type: 'city_sewer'})}
                    className="mr-2"
                  />
                  <span className="text-sm">🚽 City Sewer</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="sewer_type"
                    checked={formData.sewer_type === 'septic'}
                    onChange={() => setFormData({...formData, sewer_type: 'septic'})}
                    className="mr-2"
                  />
                  <span className="text-sm">🔧 Septic System</span>
                </label>
              </div>
            </div>

            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <h4 className="font-semibold text-gray-900 mb-3">Heating Fuel</h4>
              <Select value={formData.heating_fuel} onValueChange={(val) => setFormData({...formData, heating_fuel: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select heating fuel type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="electric">Electric</SelectItem>
                  <SelectItem value="natural_gas">Natural Gas</SelectItem>
                  <SelectItem value="propane">Propane</SelectItem>
                  <SelectItem value="dual">Dual (Gas + Electric)</SelectItem>
                  <SelectItem value="none">No Heating System</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-gray-900 mb-3">☀️ Solar Panels</h4>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.has_solar_panels}
                    onChange={(e) => setFormData({...formData, has_solar_panels: e.target.checked, solar_panel_year_installed: e.target.checked ? formData.solar_panel_year_installed : "", solar_panel_ownership: e.target.checked ? formData.solar_panel_ownership : ""})}
                    className="mr-2 h-4 w-4"
                  />
                  <span className="text-sm font-medium">Property has solar panels installed</span>
                </label>
                {formData.has_solar_panels && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ownership Status</label>
                      <Select value={formData.solar_panel_ownership} onValueChange={(val) => setFormData({...formData, solar_panel_ownership: val})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="owned">Owned (Paid Off)</SelectItem>
                          <SelectItem value="financed">Financed (Loan)</SelectItem>
                          <SelectItem value="leased">Leased</SelectItem>
                          <SelectItem value="ppa">PPA (Power Purchase Agreement)</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.solar_panel_ownership === 'leased' && (
                        <p className="text-xs text-orange-600 mt-1 font-medium">⚠️ Buyer will assume lease payments</p>
                      )}
                      {formData.solar_panel_ownership === 'ppa' && (
                        <p className="text-xs text-orange-600 mt-1 font-medium">⚠️ Buyer will assume PPA agreement</p>
                      )}
                      {formData.solar_panel_ownership === 'financed' && (
                        <p className="text-xs text-blue-600 mt-1">ℹ️ Loan must be paid off or assumed</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Year Installed</label>
                      <Input
                        type="number"
                        value={formData.solar_panel_year_installed}
                        onChange={(e) => setFormData({...formData, solar_panel_year_installed: e.target.value})}
                        placeholder="2022"
                        min="1990"
                        max={new Date().getFullYear()}
                      />
                      <p className="text-xs text-gray-500 mt-1">For warranty info</p>
                    </div>
                  </div>
                )}
                {!formData.has_solar_panels && (
                  <p className="text-xs text-gray-500">Solar panels can significantly reduce energy costs</p>
                )}
              </div>
            </div>

            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-gray-900 mb-3">💵 Average Monthly Utility Costs</h4>
              <p className="text-xs text-gray-600 mb-3">Help buyers budget by sharing your average monthly costs</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Electric ($)</label>
                  <Input
                    type="number"
                    value={formData.monthly_electric_cost}
                    onChange={(e) => setFormData({...formData, monthly_electric_cost: e.target.value})}
                    placeholder="150"
                  />
                  <p className="text-xs text-gray-500 mt-1">Average monthly</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gas ($)</label>
                  <Input
                    type="number"
                    value={formData.monthly_gas_cost}
                    onChange={(e) => setFormData({...formData, monthly_gas_cost: e.target.value})}
                    placeholder="60"
                  />
                  <p className="text-xs text-gray-500 mt-1">If applicable</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Water/Sewer ($)</label>
                  <Input
                    type="number"
                    value={formData.monthly_water_sewer_cost}
                    onChange={(e) => setFormData({...formData, monthly_water_sewer_cost: e.target.value})}
                    placeholder="80"
                  />
                  <p className="text-xs text-gray-500 mt-1">Combined bill</p>
                </div>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="space-y-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 border-b pb-3">Location</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Street Address *</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="123 Main Street"
                data-testid="input-address"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder="Dallas"
                  data-testid="input-city"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">County *</label>
                <Select value={formData.county} onValueChange={(val) => setFormData({...formData, county: val})}>
                  <SelectTrigger data-testid="input-county">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {counties.map(county => (
                      <SelectItem key={county} value={county}>{county}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code *</label>
                <Input
                  value={formData.zip_code}
                  onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                  placeholder="75001"
                  data-testid="input-zipcode"
                />
              </div>
            </div>
          </div>

          {/* Property Details */}
          <div className="space-y-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 border-b pb-3">Property Details</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Property Type *</label>
                <Select value={formData.property_type} onValueChange={(val) => setFormData({...formData, property_type: val})}>
                  <SelectTrigger data-testid="input-property-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Single Family">Single Family</SelectItem>
                    <SelectItem value="Townhouse">Townhouse</SelectItem>
                    <SelectItem value="Condo">Condo</SelectItem>
                    <SelectItem value="Multi-Family">Multi-Family</SelectItem>
                    <SelectItem value="Land">Land</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Year Built</label>
                <Input
                  type="number"
                  value={formData.year_built}
                  onChange={(e) => setFormData({...formData, year_built: e.target.value})}
                  placeholder="2020"
                  data-testid="input-year-built"
                />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bedrooms *</label>
                <Input
                  type="number"
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({...formData, bedrooms: e.target.value})}
                  placeholder="4"
                  data-testid="input-bedrooms"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bathrooms *</label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({...formData, bathrooms: e.target.value})}
                  placeholder="2.5"
                  data-testid="input-bathrooms"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sqft *</label>
                <Input
                  type="number"
                  value={formData.sqft}
                  onChange={(e) => setFormData({...formData, sqft: e.target.value})}
                  placeholder="2500"
                  data-testid="input-sqft"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lot Size (acres)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.lot_size}
                  onChange={(e) => setFormData({...formData, lot_size: e.target.value})}
                  placeholder="0.25"
                  data-testid="input-lot-size"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Features (comma-separated)</label>
              <Input
                value={formData.features}
                onChange={(e) => setFormData({...formData, features: e.target.value})}
                placeholder="Hardwood Floors, Updated Kitchen, Large Backyard, Fireplace"
                data-testid="input-features"
              />
            </div>

            {/* Pool */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-gray-900 mb-3">🏊 Swimming Pool</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      checked={formData.has_pool}
                      onChange={(e) => setFormData({...formData, has_pool: e.target.checked, pool_type: e.target.checked ? formData.pool_type : ""})}
                      className="mr-2 h-4 w-4"
                    />
                    <span className="text-sm font-medium">Property has a swimming pool</span>
                  </label>
                </div>
                {formData.has_pool && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pool Type</label>
                    <Select value={formData.pool_type} onValueChange={(val) => setFormData({...formData, pool_type: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Inground Gunite">Inground Gunite</SelectItem>
                        <SelectItem value="Inground Vinyl">Inground Vinyl</SelectItem>
                        <SelectItem value="Inground Fiberglass">Inground Fiberglass</SelectItem>
                        <SelectItem value="Above Ground">Above Ground</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Garage */}
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-gray-900 mb-3">🚗 Garage / Parking</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Garage Spaces</label>
                  <Select value={formData.garage_spaces} onValueChange={(val) => setFormData({...formData, garage_spaces: val})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">No Garage</SelectItem>
                      <SelectItem value="1">1 Car</SelectItem>
                      <SelectItem value="2">2 Car</SelectItem>
                      <SelectItem value="3">3 Car</SelectItem>
                      <SelectItem value="4">4+ Car</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.garage_spaces && formData.garage_spaces !== "0" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Garage Type</label>
                    <Select value={formData.garage_type} onValueChange={(val) => setFormData({...formData, garage_type: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Attached">Attached</SelectItem>
                        <SelectItem value="Detached">Detached</SelectItem>
                        <SelectItem value="Carport">Carport</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Schools */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-gray-900 mb-3">🎓 School Information</h4>
              <p className="text-xs text-gray-600 mb-3">Providing school info helps families make decisions</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">School District</label>
                  <Input
                    value={formData.school_district}
                    onChange={(e) => setFormData({...formData, school_district: e.target.value})}
                    placeholder="e.g., Plano ISD, Frisco ISD, Dallas ISD"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Elementary School</label>
                  <Input
                    value={formData.elementary_school}
                    onChange={(e) => setFormData({...formData, elementary_school: e.target.value})}
                    placeholder="School name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Middle School</label>
                  <Input
                    value={formData.middle_school}
                    onChange={(e) => setFormData({...formData, middle_school: e.target.value})}
                    placeholder="School name"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">High School</label>
                  <Input
                    value={formData.high_school}
                    onChange={(e) => setFormData({...formData, high_school: e.target.value})}
                    placeholder="School name"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          {/* CRITICAL BUYER SAFETY WARNING */}
          <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-8 rounded-r-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-4">
                <h4 className="text-lg font-bold text-red-900 mb-2">⚠️ CRITICAL: Vet ALL Buyers Before Showings</h4>
                <div className="text-sm text-red-800 space-y-2">
                  <p className="font-semibold">As a FSBO seller, YOU are responsible for your safety. Always:</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li><strong>Run background checks</strong> - Use criminal background services before ANY showing</li>
                    <li><strong>Verify identity</strong> - Request government ID and verify it matches buyer info</li>
                    <li><strong>Check financial readiness</strong> - Ask for pre-qualification letters from lender</li>
                    <li><strong>Never show alone</strong> - Have someone with you during all showings</li>
                    <li><strong>Meet in public first</strong> - Initial meetings at coffee shop, not your home</li>
                    <li><strong>Trust your instincts</strong> - If something feels off, cancel the showing</li>
                  </ul>
                  <p className="mt-3 font-semibold text-red-900">
                    💡 Its-Sold.com offers buyer verification badges and background check integration. 
                    Look for verified buyers with ✓ badges!
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 border-b pb-3">Contact Information</h3>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  placeholder="John Doe"
                  data-testid="input-contact-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  placeholder="john@example.com"
                  data-testid="input-contact-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  placeholder="(555) 123-4567"
                  data-testid="input-contact-phone"
                />
              </div>
            </div>

            {/* Contact Preferences */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-6">
              <h4 className="font-semibold text-gray-900 mb-3">How Should Buyers Contact You?</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Contact Method(s) *</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.preferred_contact_method.includes('email')}
                        onChange={(e) => {
                          const methods = e.target.checked 
                            ? [...formData.preferred_contact_method, 'email']
                            : formData.preferred_contact_method.filter(m => m !== 'email');
                          setFormData({...formData, preferred_contact_method: methods});
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">📧 Email</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.preferred_contact_method.includes('phone')}
                        onChange={(e) => {
                          const methods = e.target.checked 
                            ? [...formData.preferred_contact_method, 'phone']
                            : formData.preferred_contact_method.filter(m => m !== 'phone');
                          setFormData({...formData, preferred_contact_method: methods});
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">📞 Phone Call</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.preferred_contact_method.includes('sms')}
                        onChange={(e) => {
                          const methods = e.target.checked 
                            ? [...formData.preferred_contact_method, 'sms']
                            : formData.preferred_contact_method.filter(m => m !== 'sms');
                          setFormData({...formData, preferred_contact_method: methods});
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm">💬 Text/SMS</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Best Time to Contact *</label>
                  <Select value={formData.preferred_contact_time} onValueChange={(value) => setFormData({...formData, preferred_contact_time: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning (8am - 12pm)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (12pm - 5pm)</SelectItem>
                      <SelectItem value="evening">Evening (5pm - 8pm)</SelectItem>
                      <SelectItem value="anytime">Anytime</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">When buyers should reach out</p>
                </div>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg"
            data-testid="submit-listing-btn"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white mr-3"></div>
                Creating Your Listing...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Create Free Listing
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CreateListing;
